import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Brain, Castle, ListOrdered, User } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

/**
 * QClans Hub section — rendered in the game-conditional area of the Hub when
 * Clash of Clans is the selected game (mirrors ChessHubSection / the CR
 * section). A compact 2×2 grid of entry tiles to the CoC sub-screens
 * (profile / clan / competitions / coach). Each screen handles its own
 * "link first" state on entry, so no link CTA is duplicated here.
 */
export function CocHubSection() {
  const { t } = useTranslation('coc');

  return (
    <div className="grid grid-cols-2 gap-3">
      <CocNav
        to="/coc/profile"
        icon={<User aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
        label={t('home.nav.profile')}
      />
      <CocNav
        to="/coc/clan"
        icon={<Castle aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
        label={t('home.nav.clan')}
      />
      <CocNav
        to="/coc/competitions"
        icon={<ListOrdered aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
        label={t('home.nav.competitions')}
      />
      <CocNav
        to="/coc/coach"
        icon={<Brain aria-hidden className="h-5 w-5" strokeWidth={2.5} />}
        label={t('home.nav.coach')}
      />
    </div>
  );
}

function CocNav({ to, icon, label }: { to: string; icon: ReactNode; label: string }) {
  const haptics = useHaptics();
  return (
    <Link
      to={to}
      onClick={() => haptics.impact('light')}
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-2xl border border-brand/40 bg-brand/5 px-2 py-3 text-center',
        'transition-opacity active:opacity-80',
      )}
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
        {icon}
      </span>
      <span className="text-[11px] font-bold text-fg">{label}</span>
    </Link>
  );
}
